import React, {
    useCallback, useEffect, useMemo
} from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { PolicyService } from 'gw-capability-policy';
import { WniDocumentRetrievalService } from 'wni-capability-gateway';
import { useModal } from '@jutro/components';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import FileUploadViewerComponent from '../PolicyCommonDocuments/FileUploadViewerComponent/FileUploadViewerComponent';
import metadata from './CECLPolicyCommonDocuments.metadata.json5';
import messages from './CECLPolicyCommonDocuments.messages';
import styles from './CECLPolicyCommonDocuments.module.scss';

function CECLPolicyCommonDocuments(props) {
    const modalApi = useModal();
    const {
        value: { documentDTOs: policyCommonDocuments, policyNumber, canUploadDocument },
        writableDocuments,
        updateWritableDocuments
    } = props;
    const { authHeader } = useAuthentication();

    const publicDocuments = useMemo(() => {
        return _.filter(policyCommonDocuments, (document) => {
            return !document.isAuthor;
        });
    }, [policyCommonDocuments]);

    useEffect(() => {
        const writableDoc = _.filter(policyCommonDocuments, (document) => {
            return document.isAuthor;
        });
        updateWritableDocuments(writableDoc);
    }, [policyCommonDocuments]);

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await PolicyService.getDocumentUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return modalApi.showAlert({
                title: commonMessages.errorUploadTitle,
                message: commonMessages.errorGenerateUploadToken,
                status: 'error',
                icon: 'gw-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }
    }, [authHeader]);

    const deleteDocument = useCallback(
        (item) => {
            if (!item.canDelete) {
                return;
            }

            const { publicID } = item;
            modalApi.showConfirm({
                title: messages.removeDocument,
                message: messages.confirmRemoveDocument,
                status: 'warning',
                icon: 'gw-error-outline',
                confirmButtonText: messages.documentRemoveConfirmYes,
                cancelButtonText: messages.documentRemoveConfirmNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                try {
                    const isDocumentDeleted = await PolicyService.removeDocument(
                        publicID,
                        authHeader
                    );
                    if (isDocumentDeleted) {
                        const newDocuments = _.filter(writableDocuments, (doc) => {
                            return doc.workingPublicID !== publicID;
                        });
                        updateWritableDocuments(newDocuments);
                    }
                } catch (documentDeletionError) {
                    modalApi.showAlert({
                        title: messages.documentDeletionFailed,
                        message: messages.documentCannotDeleted,
                        status: 'error',
                        icon: 'gw-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                }
                return true;
            }, _.noop);
        },
        [authHeader, modalApi, updateWritableDocuments, writableDocuments]
    );

    const getDocumentDownloadLink = useCallback(
        (event, item) => {
            event.preventDefault();
            const { publicID, name } = item;
            return WniDocumentRetrievalService.getOnDemandDocument(publicID, name, authHeader)
        },
        []
    );

    const uploadDocument = useCallback(
        async (item) => {
            const documentMetaDataTemplate = {
                docUID: '001',
                documentType: 'letter_received',
                securityType: 'unrestricted',
                status: 'approved',
                policyNumber: policyNumber,
                name: item.name,
                mimeType: item.type,
                SessionID: await claimsDocUploadToken()
            };
            try {
                const recentUploadedDoc = await PolicyService.uploadDocument(
                    item,
                    documentMetaDataTemplate,
                    authHeader
                );
                if (!_.isEmpty(recentUploadedDoc)) {
                    updateWritableDocuments((docs) => [...docs, recentUploadedDoc]);
                }
            } catch (error) {
                modalApi.showAlert({
                    title: messages.documentUploadFailed,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'gw-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
            }
        },
        [authHeader, claimsDocUploadToken, policyNumber]
    );

    const overrides = {
        policyDocuments: {
            visible: !_.isEmpty(publicDocuments)
        },
        readOnlyDocumentFileUploadComponent: {
            data: {
                isReadOnly: true,
                documents: publicDocuments
            },
            getDownloadLink: getDocumentDownloadLink
        },
        writableDocumentFileUploadComponent: {
            data: {
                isReadOnly: !canUploadDocument,
                documents: writableDocuments
            },
            getDownloadLink: getDocumentDownloadLink
        }
    };

    const resolvers = {
        resolveComponentMap: {
            documentscomponent: FileUploadViewerComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onDocumentDelete: deleteDocument,
            onDocumentUpload: uploadDocument
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(
                metadata.componentContent,
                policyCommonDocuments,
                id,
                path,
                overrides
            );
        },
        [policyCommonDocuments, overrides]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyCommonDocuments}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            resolveValue={readValue}
        />
    );
}

CECLPolicyCommonDocuments.propTypes = {
    value: PropTypes.shape({
        documentDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        policyNumber: PropTypes.string,
        canUploadDocument: PropTypes.bool
    }),
    writableDocuments: PropTypes.shape([]).isRequired,
    updateWritableDocuments: PropTypes.shape([]).isRequired
};

CECLPolicyCommonDocuments.defaultProps = {
    value: {
        documentDTOs: [],
        policyNumber: '',
        canUploadDocument: false
    }
};

export default withRouter(CECLPolicyCommonDocuments);
